import { useEffect, useRef, useState } from "react";
import { catalogApi } from "../../api/catalogApi";
import { createSchedulingOffering, updateSchedulingOffering } from "../../api/scheduling";
import { useEditorDraft } from "./useSetupDrafts";
import { controlClass, selectClass, primaryButtonClass } from "./schedulingControlStyles";

const field = `mt-1 block w-full ${controlClass}`;
const selectField = `mt-1 block w-full ${selectClass}`;
function Input({label,value,set,type="text",required=true,min,max}) { return <label className="block min-w-0 text-xs font-medium text-slate-600">{label}<input type={type} required={required} min={min} max={max} value={value??""} onChange={e=>set(e.target.value)} className={field}/></label>; }

const emptyOffering = {
  name: "",
  description: "",
  defaultDurationMinutes: 60,
  bufferBeforeMinutes: 0,
  bufferAfterMinutes: 0,
  catalogId: "",
  catalogItemId: "",
  catalogItemVariantId: "",
};

const isServiceItem = item => String(item?.kind || "").toLowerCase() === "service";
const inactiveSuffix = entity => entity?.isActive ? "" : " (Inactive)";

export default function SchedulingServiceDetails({ value, initial = {}, disabled, onSave, label = "Save duration & continue", formId, hideSubmit = false, associationLabel }) {
  const items = value ? [value] : [];
  const editing = value?.id ? value : null;
  const [form,setForm]=useState(() => Object.fromEntries(Object.entries(emptyOffering).map(([key,fallback])=>[key,value?.[key] ?? initial[key] ?? fallback])));
  const initialForm = useRef(form).current;
  const submitDraft = useEditorDraft(form, initialForm);
  const [catalogs,setCatalogs]=useState([]);
  const [catalogItems,setCatalogItems]=useState({});
  const [catalogVariants,setCatalogVariants]=useState({});
  const [catalogError,setCatalogError]=useState("");
  const [formError,setFormError]=useState("");

  useEffect(()=>{
    let current=true;
    catalogApi.listAllCatalogs({activeOnly:false})
      .then(result=>{if(current)setCatalogs(result);})
      .catch(()=>{if(current)setCatalogError("Catalogue choices could not be loaded. Unlinked offerings can still be managed.");});
    return()=>{current=false;};
  },[]);

  const catalogIds=[...new Set([
    ...items.map(x=>x.catalogId),
    form.catalogId,
  ].filter(Boolean))];
  const catalogIdsKey=catalogIds.sort().join(",");
  useEffect(()=>{
    let current=true;
    const missing=catalogIds.filter(id=>!Object.prototype.hasOwnProperty.call(catalogItems,id));
    if(!missing.length)return()=>{current=false;};
    Promise.all(missing.map(async id=>[id,await catalogApi.listAllItems(id,{activeOnly:false})]))
      .then(entries=>{if(current)setCatalogItems(previous=>({...previous,...Object.fromEntries(entries)}));})
      .catch(()=>{if(current)setCatalogError("Catalogue services could not be loaded. Try again before changing an association.");});
    return()=>{current=false;};
    // The stable key prevents reloads caused only by array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[catalogIdsKey]);

  const variantPairs=[...new Set([
    ...items.filter(x=>x.catalogId&&x.catalogItemId).map(x=>`${x.catalogId}/${x.catalogItemId}`),
    form.catalogId&&form.catalogItemId?`${form.catalogId}/${form.catalogItemId}`:null,
  ].filter(Boolean))];
  const variantPairsKey=variantPairs.sort().join(",");
  useEffect(()=>{
    let current=true;
    const missing=variantPairs.filter(key=>!Object.prototype.hasOwnProperty.call(catalogVariants,key));
    if(!missing.length)return()=>{current=false;};
    Promise.all(missing.map(async key=>{
      const [catalogId,itemId]=key.split("/");
      return [key,await catalogApi.listAllVariants(catalogId,itemId,{activeOnly:false})];
    }))
      .then(entries=>{if(current)setCatalogVariants(previous=>({...previous,...Object.fromEntries(entries)}));})
      .catch(()=>{if(current)setCatalogError("Catalogue variants could not be loaded. Try again before changing an association.");});
    return()=>{current=false;};
    // The stable key prevents reloads caused only by array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[variantPairsKey]);

  const originalCatalogId=editing?.catalogId||"";
  const originalItemId=editing?.catalogItemId||"";
  const originalVariantId=editing?.catalogItemVariantId||"";
  const selectedCatalogItems=catalogItems[form.catalogId]||[];
  const selectedVariantKey=form.catalogId&&form.catalogItemId?`${form.catalogId}/${form.catalogItemId}`:"";
  const selectedVariants=catalogVariants[selectedVariantKey]||[];
  const availableCatalogs=catalogs.filter(x=>x.isActive||x.id===originalCatalogId);
  const availableServices=selectedCatalogItems.filter(x=>
    isServiceItem(x)&&(x.isActive||(x.id===originalItemId&&x.catalogId===originalCatalogId))
  );
  const availableVariants=selectedVariants.filter(x=>
    x.isActive||(x.id===originalVariantId&&form.catalogId===originalCatalogId&&form.catalogItemId===originalItemId)
  );

  const changeCatalog=catalogId=>setForm(previous=>({...previous,catalogId,catalogItemId:"",catalogItemVariantId:""}));
  const changeCatalogItem=async catalogItemId=>{
    const catalogId=form.catalogId;
    setForm(previous=>({...previous,catalogItemId,catalogItemVariantId:""}));
    if(!catalogId||!catalogItemId)return;
    const key=`${catalogId}/${catalogItemId}`;
    try{
      const variants=catalogVariants[key]||await catalogApi.listAllVariants(catalogId,catalogItemId,{activeOnly:false});
      setCatalogVariants(previous=>({...previous,[key]:variants}));
      const defaultVariant=variants.find(x=>x.isActive&&x.isDefault);
      if(defaultVariant)setForm(previous=>previous.catalogId===catalogId&&previous.catalogItemId===catalogItemId
        ?{...previous,catalogItemVariantId:defaultVariant.id}:previous);
    }catch{
      setCatalogError("Catalogue variants could not be loaded. Try again before changing an association.");
    }
  };
  const submit=async ()=>{
    setFormError("");
    const catalogId=form.catalogId||null;
    const catalogItemId=form.catalogItemId||null;
    const catalogItemVariantId=form.catalogItemVariantId||null;
    if(!(Boolean(catalogId)===Boolean(catalogItemId)&&Boolean(catalogId)===Boolean(catalogItemVariantId))){
      setFormError("Choose a Catalogue Service and Variant, or clear the Catalogue to leave this booking service unlinked.");
      return;
    }
    if(catalogId){
      const chosenCatalog=catalogs.find(x=>x.id===catalogId);
      const chosenItem=(catalogItems[catalogId]||[]).find(x=>x.id===catalogItemId);
      const chosenVariant=(catalogVariants[`${catalogId}/${catalogItemId}`]||[]).find(x=>x.id===catalogItemVariantId);
      const isExistingLink=catalogId===originalCatalogId&&catalogItemId===originalItemId&&catalogItemVariantId===originalVariantId;
      if(!chosenCatalog||!chosenItem||chosenItem.catalogId!==catalogId||!isServiceItem(chosenItem)){
        setFormError("Choose a valid Service from the selected Catalogue.");
        return;
      }
      if(!chosenVariant||chosenVariant.catalogId!==catalogId||(chosenVariant.itemId ?? chosenVariant.catalogItemId)!==catalogItemId){
        setFormError("Choose a valid Variant from the selected Catalogue Service.");
        return;
      }
      if((!chosenCatalog.isActive||!chosenItem.isActive||!chosenVariant.isActive)&&!isExistingLink){
        setFormError("New links require an active Catalogue, Service and Variant.");
        return;
      }
    }
    const payload={...form,catalogId,catalogItemId,catalogItemVariantId};
    await onSave(
      () => editing ? updateSchedulingOffering(editing.id,{...payload,expectedVersion:editing.version}) : createSchedulingOffering({...payload,isActive:true}),
      result => ({offering:result.id || editing?.id,catalog:catalogId,service:catalogItemId,variant:catalogItemVariantId,new:null}),
    );
  };
  const linkedLabel = form.catalogId && form.catalogItemId && form.catalogItemVariantId
    ? [catalogs.find(x=>x.id===form.catalogId)?.name,selectedCatalogItems.find(x=>x.id===form.catalogItemId)?.name,selectedVariants.find(x=>x.id===form.catalogItemVariantId)?.name].filter(Boolean).join(" / ") || associationLabel
    : "Not linked";
  const associationFields = <div className="grid grid-cols-2 gap-4">
    <label className="block min-w-0 text-xs font-medium text-slate-600">Catalogue<select aria-label="Catalogue" value={form.catalogId} onChange={e=>changeCatalog(e.target.value)} className={selectField}><option value="">Not linked</option>{availableCatalogs.map(x=><option key={x.id} value={x.id}>{x.name}{inactiveSuffix(x)}</option>)}</select></label>
    <label className="block min-w-0 text-xs font-medium text-slate-600">Catalogue Service<select aria-label="Catalogue Service" value={form.catalogItemId} onChange={e=>changeCatalogItem(e.target.value)} disabled={!form.catalogId} className={selectField}><option value="">{form.catalogId?"Select service...":"Select a catalogue first"}</option>{availableServices.map(x=><option key={x.id} value={x.id}>{x.name}{inactiveSuffix(x)}</option>)}</select></label>
    <label className="block min-w-0 text-xs font-medium text-slate-600">Variant<select aria-label="Variant" value={form.catalogItemVariantId} onChange={e=>setForm({...form,catalogItemVariantId:e.target.value})} disabled={!form.catalogItemId} className={selectField}><option value="">{form.catalogItemId?"Select variant...":"Select a service first"}</option>{availableVariants.map(x=><option key={x.id} value={x.id}>{x.name}{x.isDefault?" (Default)":""}{inactiveSuffix(x)}</option>)}</select></label>
  </div>;
  return <div className="space-y-5">
    {catalogError&&<p role="status" className="rounded bg-amber-50 p-3 text-sm text-amber-900">{catalogError}</p>}
    <form id={formId} onSubmit={event=>{event.preventDefault();submitDraft(submit);}}><fieldset disabled={disabled} className="grid grid-cols-2 gap-4">
      <Input label="Name" value={form.name} set={v=>setForm({...form,name:v})}/>
      <Input type="number" min={1} max={1440} label="Duration minutes" value={form.defaultDurationMinutes} set={v=>setForm({...form,defaultDurationMinutes:Number(v)})}/>
      <div className="col-span-2"><Input label="Description" required={false} value={form.description} set={v=>setForm({...form,description:v})}/></div>
      <details className="col-span-2 rounded-lg border border-slate-200 p-3"><summary className="cursor-pointer text-sm font-semibold text-slate-700">Advanced settings</summary><p className="mt-2 text-xs text-slate-500">Reserve extra time before or after the service for preparation and cleanup.</p><div className="mt-3 grid grid-cols-2 gap-4"><Input type="number" min={0} max={1440} label="Buffer before" value={form.bufferBeforeMinutes} set={v=>setForm({...form,bufferBeforeMinutes:Number(v)})}/><Input type="number" min={0} max={1440} label="Buffer after" value={form.bufferAfterMinutes} set={v=>setForm({...form,bufferAfterMinutes:Number(v)})}/></div></details>
      <div className="col-span-2">{associationLabel ? <div className="rounded-lg border border-slate-200 bg-slate-50 p-3"><p className="text-xs text-slate-500">Linked service option</p><p className="mt-1 text-sm font-medium text-slate-700">{linkedLabel}</p><details className="mt-3"><summary className="cursor-pointer text-xs font-semibold text-emerald-700">Change linked service</summary><div className="mt-3">{associationFields}</div></details></div> : associationFields}</div>
      {!hideSubmit && <div><button className={primaryButtonClass} disabled={disabled}>{label}</button></div>}
      {formError&&<p role="alert" className="col-span-2 rounded bg-rose-50 p-3 text-sm text-rose-800">{formError}</p>}
    </fieldset></form>
  </div>;
}
